import { useMemo } from "react"
import PropTypes from "prop-types"
import { isEqual } from "lodash"
import ContributorsDataIntegrator from "gatherers/contributors-data-integrator"
import { isInstrumentWithoutGMV } from "helpers/metrics/utils"
import { CLASSIFICATIONS_SHAPE } from "helpers/shapes/asset-view"
import { useTemplateGatherers } from "connected-components/dashboard/hooks/use-template-gatherers"
import { useAppSelector } from "hooks/redux"
import { useDashboardId } from "connected-components/dashboard/context/dashboard-id"
import { DEFAULT_CORE_STATE } from "connected-components/dashboard/store/core-slice"
import { DEFAULT_TRADE_STATE } from "connected-components/dashboard/store/trade-slice"
import { useTradeActions } from "connected-components/dashboard/hooks/use-trade-actions"
import NewTrade from "./renderers"

// [TODO-JG] Support trading a specific security within a group
export default function AddTrade({ classifications, currentGrouping = null, hasFilters = false }) {
  const dashboardId = useDashboardId()

  const coreState = useAppSelector((s) => s.dashboard.core[dashboardId] ?? DEFAULT_CORE_STATE)
  const modelId = useAppSelector((s) => s.dashboard.model[dashboardId]?.modelId ?? null)
  const tradeState = useAppSelector((s) => s.dashboard.trade[dashboardId] ?? DEFAULT_TRADE_STATE)

  const {
    instrument,
    base,
    filter,
    normalization,
    secondInstrument,
    comparisonInstrument,
    selectedMode,
    endDate,
    startDate,
    intradayBatchMetadata,
    intradayPricingAsOf,
    latestDate,
    isEndVsStartDateComparison,
    instrumentsMetadata,
  } = coreState

  const { trades, actionsBase, tradedPosition, tradedPositionField, isExpanded } = tradeState

  const { addTrade, setTradedPosition } = useTradeActions()

  const hideEconomicExposure = useMemo(() => isInstrumentWithoutGMV(instrument?.type), [instrument])

  const contextData = useMemo(() => ({
    modelId,
    instrument,
    base,
    filter,
    normalization,
    secondInstrument,
    comparisonInstrument,
    selectedMode,
    endDate,
    startDate,
    intradayBatchMetadata,
    intradayPricingAsOf,
    latestDate,
    isEndVsStartDateComparison,
    instrumentsMetadata,
    trades,
    actionsBase,
    isExpanded,
  }), [modelId, instrument, base, filter, normalization, secondInstrument, comparisonInstrument, selectedMode, endDate, startDate, intradayBatchMetadata, intradayPricingAsOf, latestDate, isEndVsStartDateComparison, instrumentsMetadata, trades, actionsBase, isExpanded])

  const gatherers = useTemplateGatherers(contextData, "trade", {
    classifications,
    target: secondInstrument,
  })

  return (
    <ContributorsDataIntegrator
      gatherers={gatherers}
      Renderer={NewTrade}
      rendererProps={{
        modelId,
        base,
        addTrade,
        tradedPosition,
        tradedPositionField,
        setTradedPosition,
        hideEconomicExposure,
        filter,
        isActiveTradingDisabled: actionsBase && !isEqual(actionsBase, base),
        isExpanded,
        trades,
        currentGrouping,
        hasFilters,
      }}
    />
  )
}

AddTrade.propTypes = {
  classifications: CLASSIFICATIONS_SHAPE.isRequired,
  currentGrouping: PropTypes.object,
  hasFilters: PropTypes.bool,
}
